import { useState, useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { api } from '@/modules/shared/lib/axios'
import { useOrderFormStoreActions } from '@/modules/order/stores/order-form.store'
import { useMainStore } from '@/modules/shared/stores/main.store'
import { type Entity } from '@/modules/shared/types/entity.type'
import { useCustomerFormStoreAction } from '@/modules/customer/stores/customer-form.store'

// Tipos para as rotas
export type Route = {
  idRota: number
  descricao: string
  diaVisita: string
  dataInclusaoServidor: string
}

// Tipo para cliente
export type RouteClient = {
  idEntidade: number
  razaoSocialNome: string
  logradouro: string
  numero: string
  complemento: string
  bairro: string
  fone: string
  contato: string
  cidade: string
  uf: string
  flagSituacao: boolean
  cnpjCpf: string
  dataEmissao: string
  valorTotal: number
  situacao: string
  email: string
  permiteAlteracao: boolean
  permiteExclusao: boolean
  pedidoRealizado: boolean
  flagPessoa: string
  fkTabelaPreco: string
  idTabelaPrecoVigencia: string
  fkFrete: string
  fkTipoFaturamento: string
}

// Tipo para a resposta do backend
type ResponseAPI = {
  status: number
  message: string
  body: string
}

type ParsedTablesData = {
  listaRota: Route[]
}

export function useRouteClients() {
  const navigate = useNavigate()
  const location = useLocation()

  const { setIsLoading, setIsCustomerStartedOrder, setInitialLocationForOrder } =
    useMainStore().actions
  const { setOperationType: setOperationTypeOrder } = useOrderFormStoreActions()
  const { setCustomerSelected } = useCustomerFormStoreAction!

  const [routes, setRoutes] = useState<Route[]>([])
  const [routeClients, setRouteClients] = useState<RouteClient[]>([])
  const [filteredClients, setFilteredClients] = useState<RouteClient[]>([])
  const [selectedRouteId, setSelectedRouteId] = useState<number | null>(null)
  const [selectedClient, setSelectedClient] = useState<RouteClient | null>(null)
  const [isLoadingRoutes, setIsLoadingRoutes] = useState(false)
  const [isLoadingClients, setIsLoadingClients] = useState(false)
  const [routesError, setRoutesError] = useState<string | null>(null)
  const [clientsError, setClientsError] = useState<string | null>(null)
  const [searchTerm, setSearchTerm] = useState('')

  // Chave para o localStorage
  const SELECTED_ROUTE_KEY = 'selectedRouteId'

  // Função para obter o dia da semana atual (1=Domingo, 2=Segunda, etc.)
  const getCurrentDayOfWeek = () => {
    const today = new Date()
    return today.getDay() + 1 // getDay() retorna 0-6, então somamos 1 para ficar 1-7
  }

  // Função para salvar a rota selecionada no localStorage
  const saveSelectedRouteToLocalStorage = (routeId: number) => {
    try {
      localStorage.setItem(SELECTED_ROUTE_KEY, routeId.toString())
    } catch (error) {
      console.error('Erro ao salvar rota no localStorage:', error)
    }
  }

  // Função para carregar a rota selecionada do localStorage
  const loadSelectedRouteFromLocalStorage = (): number | null => {
    try {
      const savedRouteId = localStorage.getItem(SELECTED_ROUTE_KEY)
      return savedRouteId ? Number(savedRouteId) : null
    } catch (error) {
      console.error('Erro ao carregar rota do localStorage:', error)
      return null
    }
  }

  // Função para encontrar a rota correspondente ao dia da semana
  const findRouteByDayOfWeek = (routes: Route[], dayOfWeek: number) => {
    return routes.find(route => Number(route.diaVisita) === dayOfWeek)
  }

  // Atualizar o estado de carregamento global quando os estados locais mudarem
  useEffect(() => {
    if (isLoadingRoutes || isLoadingClients) {
      setIsLoading(true)
    } else {
      setIsLoading(false)
    }
  }, [isLoadingRoutes, isLoadingClients, setIsLoading])

  // Carregar rotas ao inicializar o hook
  useEffect(() => {
    loadRoutes()
  }, [])

  // Selecionar rota baseada no localStorage ou no dia da semana atual
  useEffect(() => {
    if (routes.length > 0 && !isLoadingRoutes && selectedRouteId === null) {
      // Tenta carregar a rota salva no localStorage
      const savedRouteId = loadSelectedRouteFromLocalStorage()

      // Verifica se a rota salva existe na lista de rotas
      const savedRouteExists = savedRouteId && routes.some(route => route.idRota === savedRouteId)

      let routeIdToSelect: number

      if (savedRouteExists) {
        routeIdToSelect = savedRouteId
      } else {
        // Se não houver rota salva ou ela não existir mais, seleciona baseado no dia da semana
        const currentDayOfWeek = getCurrentDayOfWeek()
        const routeForToday = findRouteByDayOfWeek(routes, currentDayOfWeek)

        if (routeForToday) {
          routeIdToSelect = routeForToday.idRota
        } else {
          // Se não encontrar a rota do dia atual, seleciona a primeira disponível
          routeIdToSelect = routes[0].idRota
        }
        // Salva a nova rota selecionada no localStorage
        saveSelectedRouteToLocalStorage(routeIdToSelect)
      }

      setSelectedRouteId(routeIdToSelect)
    }
  }, [routes, isLoadingRoutes, selectedRouteId])

  // Carregar clientes quando a rota muda
  useEffect(() => {
    if (selectedRouteId) {
      loadRouteClients(selectedRouteId)
    }
  }, [selectedRouteId])

  // Filtrar clientes baseado no termo de busca (este filtro é feito na tela, não no backend)
  useEffect(() => {
    if (!routeClients || routeClients.length === 0) {
      setFilteredClients([])
      return
    }

    if (!searchTerm.trim()) {
      setFilteredClients(routeClients)
      return
    }

    const lowercaseSearch = searchTerm.toLowerCase().trim()
    const filtered = routeClients.filter(client =>
      client.razaoSocialNome.toLowerCase().includes(lowercaseSearch) ||
      client.cnpjCpf.includes(lowercaseSearch) ||
      (client.cidade && client.cidade.toLowerCase().includes(lowercaseSearch)) ||
      (client.bairro && client.bairro.toLowerCase().includes(lowercaseSearch))
    )

    setFilteredClients(filtered)
  }, [routeClients, searchTerm])

  /**
   * Carrega a lista de rotas do backend
   */
  const loadRoutes = async () => {
    setIsLoadingRoutes(true)
    setRoutesError(null)

    try {
      // O ID da empresa é fixo como 0 conforme especificado
      const companyId = 0
      const { data } = await api.get<ResponseAPI>(`tabelascadastro/${companyId}`)

      if (data && data.status === 200) {
        // Parsear o conteúdo do body que é uma string JSON
        const parsedData: ParsedTablesData = JSON.parse(data.body)

        // Verificar se existe a lista de rotas
        if (parsedData.listaRota && Array.isArray(parsedData.listaRota)) {
          setRoutes(parsedData.listaRota)
        } else {
          setRoutes([])
        }
      } else {
        throw new Error(data?.message || 'Erro ao carregar rotas')
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Erro ao carregar rotas'
      setRoutesError(errorMessage)
      toast.error(errorMessage)
      console.error('Erro ao carregar rotas:', err)
    } finally {
      setIsLoadingRoutes(false)
    }
  }

  /**
   * Carrega os clientes de uma rota específica
   */
  const loadRouteClients = async (routeId: number) => {
    if (!routeId) return;

    setIsLoadingClients(true)
    setClientsError(null)
    setSelectedClient(null) // Limpa o cliente selecionado ao mudar de rota

    try {
      // Chamada à API para carregar clientes de uma rota
      const { data } = await api.get<ResponseAPI>(`rotacliente/${routeId}`)

      if (data && data.status === 200) {
        try {
          // Parsear o conteúdo do body que é uma string JSON
          const clients: RouteClient[] = JSON.parse(data.body)

          if (Array.isArray(clients)) {
            setRouteClients(clients)
            setFilteredClients(clients)
          } else {
            setRouteClients([])
            setFilteredClients([])
          }
        } catch (parseError) {
          console.error('Erro ao parsear clientes:', parseError)
          setRouteClients([])
          setFilteredClients([])
          setClientsError('Erro ao processar dados dos clientes')
        }
      } else {
        throw new Error(data?.message || 'Erro ao carregar clientes da rota')
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Erro ao carregar clientes da rota'
      setClientsError(errorMessage)
      toast.error(errorMessage)
      console.error('Erro ao carregar clientes da rota:', err)
      setRouteClients([])
      setFilteredClients([])
    } finally {
      setIsLoadingClients(false)
    }
  }

  /**
   * Seleciona uma rota pelo ID e carrega seus clientes
   */
  const selectRoute = (routeId: number) => {
    if (routeId !== selectedRouteId) {
      setSelectedRouteId(routeId)
      setSelectedClient(null)
      // Limpa o cliente selecionado no store global
      setCustomerSelected(null)
      // Salva a rota selecionada no localStorage
      saveSelectedRouteToLocalStorage(routeId)
    }
  }

  /**
   * Seleciona um cliente para ações
   */
  const selectClient = (client: RouteClient) => {
    if (client === selectedClient) {
      setSelectedClient(null)
      // Limpa o cliente selecionado no store global
      setCustomerSelected(null)
    } else {
      setSelectedClient(client)
      // Converte o RouteClient para Entity e define no store global
      const clientEntity: Entity = {
        idEntidade: client.idEntidade,
        razaoSocialNome: client.razaoSocialNome,
        logradouro: client.logradouro,
        numero: client.numero,
        complemento: client.complemento,
        bairro: client.bairro,
        fone: client.fone,
        contato: client.contato,
        cidade: client.cidade,
        uf: client.uf,
        flagSituacao: client.flagSituacao,
        cnpjCpf: client.cnpjCpf,
        dataEmissao: client.dataEmissao,
        valorTotal: client.valorTotal,
        situacao: client.situacao,
        email: client.email,
        permiteAlteracao: client.permiteAlteracao,
        permiteExclusao: client.permiteExclusao,
        flagPessoa: client.flagPessoa,
        idTabelaPrecoVigencia: client.idTabelaPrecoVigencia,
      }
      setCustomerSelected(clientEntity)
    }
  }

  /**
   * Manipula a pesquisa de clientes (filtragem feita na tela)
   */
  const handleSearch = (term: string) => {
    setSearchTerm(term)
  }

  /**
   * Abre o Google Maps com o endereço do cliente
   */
  const openGoogleMaps = () => {
    if (selectedClient) {
      const address = `${selectedClient.logradouro}, ${selectedClient.numero} - ${selectedClient.bairro}, ${selectedClient.cidade} - ${selectedClient.uf}`;
      const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
      window.open(mapsUrl, '_blank');
    }
  }

  /**
   * Inicia uma ligação para o cliente
   */
  const makeCall = () => {
    if (selectedClient && selectedClient.fone) {
      // Remove caracteres não numéricos do telefone
      const cleanPhone = selectedClient.fone.replace(/\D/g, '')
      if (cleanPhone) {
        window.open(`tel:${cleanPhone}`, '_self')
      } else {
        toast.info('Número de telefone não disponível ou inválido.')
      }
    } else {
      toast.info('Número de telefone não disponível para este cliente.')
    }
  }

  /**
   * Abre o cliente de email para enviar email
   */
  const sendEmail = () => {
    if (selectedClient && selectedClient.email) {
      const subject = encodeURIComponent(`Contato - ${selectedClient.razaoSocialNome}`)
      const body = encodeURIComponent(`Olá ${selectedClient.razaoSocialNome},\n\n`)
      window.open(`mailto:${selectedClient.email}?subject=${subject}&body=${body}`, '_self')
    } else {
      toast.info('Email não disponível para este cliente.')
    }
  }

  /**
   * Navega para o perfil do cliente
   */
  const navigateToClientProfile = () => {
    if (selectedClient) {
      navigate(`/cliente/profile/${selectedClient.idEntidade}`);
    }
  }

  /**
   * Inicia um pedido para o cliente selecionado
   */
  const startOrder = () => {
    if (selectedClient) {
      // Configura os estados necessários para iniciar um pedido
      setInitialLocationForOrder('/rotas')
      setIsCustomerStartedOrder(true)
      setOperationTypeOrder('add')

      navigate('/pedido/add')
    }
  }

  /**
   * Seleciona a função a ser executada com base no tipo de ação
   */
  const selectFunctionToActions = (actionType: string) => {
    const actions = {
      openMaps: openGoogleMaps,
      makeCall: makeCall,
      sendEmail: sendEmail,
      perfil: navigateToClientProfile,
      startOrder: startOrder,
    };

    const action = actions[actionType];
    if (action) {
      action();
    }
  }

  return {
    routes,
    filteredClients,
    selectedRouteId,
    selectedClient,
    isLoading: isLoadingRoutes || isLoadingClients,
    error: routesError || clientsError,
    searchTerm,
    selectRoute,
    selectClient,
    handleSearch,
    selectFunctionToActions,
    loadRoutes
  }
}